/**
 * Notification Service
 *
 * Handles API calls for notifications and unread counts
 */

import { logger } from "@/utils/logger";

export interface UnreadCount {
  total: number;
  byConversation: Record<string, number>;
}

export interface MessageNotification {
  id: string;
  ticketId: string;
  ticketTitle: string;
  senderName: string;
  senderRole: "customer" | "manufacturer" | "admin";
  content: string;
  createdAt: string;
  isRead: boolean;
}

/**
 * Fetch unread count by conversation
 */
export async function fetchUnreadCount(userId: string): Promise<UnreadCount> {
  try {
    const response = await fetch("/api/messages/unread-count", {
      method: "GET",
      credentials: "include",
      cache: "no-store",
    });
    const payload = await response.json().catch(() => null);

    if (!response.ok || !payload?.success) {
      throw new Error(payload?.error || "Failed to fetch unread count");
    }

    return payload.data ?? { total: 0, byConversation: {} };
  } catch (err) {
    logger.error(
      "Error fetching unread count",
      {
        component: "notificationService",
        action: "fetchUnreadCount",
        userId,
      },
      err instanceof Error ? err : new Error("Unknown error")
    );
    return { total: 0, byConversation: {} };
  }
}

/**
 * Mark conversation as read
 */
export async function markConversationAsRead(
  userId: string,
  conversationId: string
): Promise<void> {
  const response = await fetch("/api/messages/mark-read", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    credentials: "include",
    body: JSON.stringify({ conversationIds: [conversationId] }),
  });
  const payload = await response.json().catch(() => null);

  if (!response.ok || !payload?.success) {
    const error = new Error(
      payload?.error || "Failed to mark conversation read"
    );
    logger.error(
      "Error updating read receipt",
      {
        component: "notificationService",
        action: "markConversationAsRead",
        conversationId,
        userId,
      },
      error
    );
    throw error;
  }
}

/**
 * Mark all conversations as read
 */
export async function markAllConversationsAsRead(
  userId: string,
  conversationIds: string[]
): Promise<void> {
  if (conversationIds.length === 0) return;

  const response = await fetch("/api/messages/mark-read", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    credentials: "include",
    body: JSON.stringify({ conversationIds }),
  });
  const payload = await response.json().catch(() => null);

  if (!response.ok || !payload?.success) {
    const error = new Error(
      payload?.error || "Failed to mark conversations read"
    );
    logger.error(
      "Error updating read receipts",
      {
        component: "notificationService",
        action: "markAllConversationsAsRead",
        userId,
      },
      error
    );
    throw error;
  }
}
